import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * NotificationBell — shows unread count badge and dropdown of notifications
 */
export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/user-notifs/my?limit=15`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      // If unauthenticated, stop polling silently to avoid 401 console spam.
      // Will resume on next page load when a session exists.
      if (err?.response?.status === 401 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await axios.post(`${API}/api/user-notifs/read/${id}`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/api/user-notifs/read-all`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-600 hover:text-indigo-600 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-unread-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
            <span className="font-semibold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-indigo-100 hover:text-white">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No notifications yet</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.notification_id}
                  className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition ${!n.read ? 'bg-indigo-50/50' : ''}`}
                  onClick={() => !n.read && markRead(n.notification_id)}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * NotificationPopup — shows as a modal on login for unread popup notifications
 */
export function NotificationPopup() {
  const [popups, setPopups] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/user-notifs/popup`, { withCredentials: true })
      .then(res => setPopups(res.data.popups || []))
      .catch(() => {});
  }, []);

  const dismiss = async () => {
    const p = popups[current];
    if (p) {
      await axios.post(`${API}/api/user-notifs/dismiss/${p.notification_id}`, {}, { withCredentials: true }).catch(() => {});
    }
    if (current < popups.length - 1) {
      setCurrent(current + 1);
    } else {
      setPopups([]);
    }
  };

  if (popups.length === 0) return null;
  const p = popups[current];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" data-testid="notification-popup-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in slide-in-from-bottom-4" data-testid="notification-popup">
        <div className="bg-gradient-to-r from-indigo-500 to-violet-600 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">{p.title}</h3>
              {popups.length > 1 && (
                <p className="text-indigo-200 text-xs">{current + 1} of {popups.length}</p>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{p.message}</p>
        </div>
        <div className="px-6 pb-5 flex justify-end">
          <button
            onClick={dismiss}
            className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors"
            data-testid="notification-popup-dismiss"
          >
            {current < popups.length - 1 ? 'Next' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * PushNotificationOptIn — asks user to enable browser push notifications
 */
export function PushNotificationOptIn() {
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
    // Show opt-in after 10 seconds
    const timer = setTimeout(() => setShow(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const enablePush = async () => {
    setStatus('subscribing');
    try {
      const reg = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      await axios.post(`${API}/api/user-notifs/push/subscribe`, {
        subscription: sub.toJSON(),
      }, { withCredentials: true });
      setStatus('done');
      setTimeout(() => setShow(false), 2000);
    } catch (e) {
      console.error('Push subscribe error:', e);
      setStatus('error');
    }
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm" data-testid="push-optin-banner">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Enable notifications?</p>
            <p className="text-xs text-gray-500 mt-0.5">Get price alerts and updates even when you're not on the app.</p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={enablePush}
                disabled={status === 'subscribing'}
                className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
                data-testid="push-optin-enable"
              >
                {status === 'subscribing' ? 'Enabling...' : status === 'done' ? 'Enabled!' : 'Enable'}
              </button>
              <button
                onClick={() => setShow(false)}
                className="text-gray-400 px-3 py-1.5 rounded-lg text-xs hover:text-gray-600"
                data-testid="push-optin-dismiss"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
